import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Output, ViewChild, effect, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subscription, combineLatest, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { EntitiesFacade, PROVIDERS_STATUS_BACKGROUND_COLORS, PROVIDERS_STATUS_COLORS } from 'src/app/abstraction/entities.facade';
import { MapsFacade } from 'src/app/abstraction/maps.facade';
import { ProvisionsFacade } from 'src/app/abstraction/provisions.facade';
import { ProviderByStatusAndLocation } from 'src/app/core/enums/ProviderByStatusAndLocation';
import { IconMapMarker } from 'src/app/core/enums/iconMapMarker';
import { PROVIDER_STATUS, PROVIDER_STATUS_TAGS } from 'src/app/core/enums/providerStatus';
import { TypeMapMarker } from 'src/app/core/enums/typeMapMarker';
import { MaterialModule } from 'src/app/material/material.module';
import { FormsModule } from 'src/app/shared/components/forms/forms.module';
import { MarkerGoogleMaps } from 'src/app/shared/models/maps/markerGoogleMaps';

export interface onCheckMarker {
    id: number;
    type: TypeMapMarker;
    visible?: boolean;
    clusterId?: number;
}

@Component({
    selector: 'app-markers-list',
    standalone: true,
    imports: [MaterialModule, FormsModule, CommonModule],
    templateUrl: './markers-list.component.html',
    styleUrl: './markers-list.component.scss',
    animations: [
        trigger('collapse', [
            state('expanded', style({
                height: '*',
                opacity: 1,
                paddingTop: '*',
                paddingBottom: '*',
                marginTop: '*',
                marginBottom: '*',
                overflow: 'hidden'
            })),
            state('collapsed', style({
                height: '0px',
                opacity: 0,
                paddingTop: 0,
                paddingBottom: 0,
                marginTop: 0,
                marginBottom: 0,
                overflow: 'hidden'
            })),
            transition('expanded <=> collapsed', animate('200ms ease'))
        ])
    ]
})

export class MarkersListComponent {
    private _markers!: Subscription;
    private _markersMembers!: Subscription;
    public loadingActive$: Observable<boolean>;
    public loadingInactivePossible$: Observable<boolean>;
    public loadingInactiveCancelled$: Observable<boolean>;
    public loadingInactive$: Observable<boolean>;
    public isLoadingGetAgreementsBySpecialtyId$: Observable<boolean>;
    public loading:boolean = false;
    private subscriptions = new Subscription();


    // signals/inputs
    actualCenter = input<MarkerGoogleMaps>();
    locationsFiltered = input<TypeMapMarker[]>(Object.values(TypeMapMarker));
    reset = input<boolean>(false);
    patientsList = input<MarkerGoogleMaps[]>([]);
    typeMarkers = input<String>();
    providerAddressVisibility = input<{ markerId: number, value: boolean }>(null);
    providerZonesVisibility = input<{ markerId: number, value: boolean }>(null);
    listFocused = input<{ focus: boolean, timeStamp: Date }>(null);

    @Output() onCheck = new EventEmitter<onCheckMarker>(null);
    @Output() locateMarker = new EventEmitter<onCheckMarker>(null);

    @ViewChild('searchInput', { static: false }) searchInputRef!: ElementRef<HTMLInputElement>;

    readonly SEARCH = 'search';
    selectForm!: FormGroup;
    //Collapse or expand list
    listVisible = true;
    // Update coalescing
    private rafId: number | null = null;
    // Source of truth for visibility
    private visibility = new Map<number, boolean>();
    private lastQuery = '';
    patientsType = false;
    // Cache of the last valid center to keep distance sorting even if actualCenter() becomes undefined
    private lastCenter: MarkerGoogleMaps | null = null;

    dataSourceMarkers: MarkerGoogleMaps[] = [];
    private _displayedMarkers: MarkerGoogleMaps[] = [];
    localMarkers: MarkerGoogleMaps[] = [];
    matchedMarkers: MarkerGoogleMaps[] = null;
    activeSearch: boolean = false;
    allClusterMembers: MarkerGoogleMaps[] = [];
    modeProviders: boolean = false;
    modePatients: boolean = false;
    patients = 'PATIENTS';
    providers = 'PROVIDERS';
    displayedColumns: string[] = ['selected', 'name', 'state', 'location'];

    IconMapMarkers = IconMapMarker;
    PROVIDERS_STATUS_COLORS = PROVIDERS_STATUS_COLORS;
    PROVIDERS_STATUS_BACKGROUND_COLORS = PROVIDERS_STATUS_BACKGROUND_COLORS;
    providerStatusTags: Record<PROVIDER_STATUS, string> = {
        [PROVIDER_STATUS.ACTIVE]: PROVIDER_STATUS_TAGS.ACTIVE,
        [PROVIDER_STATUS.INACTIVE]: PROVIDER_STATUS_TAGS.INACTIVE,
        [PROVIDER_STATUS.POSSIBLE]: PROVIDER_STATUS_TAGS.POSSIBLE,
        [PROVIDER_STATUS.CANCELLED]: PROVIDER_STATUS_TAGS.CANCELLED,
    };

    // Providers Types to exclude from the list
    typesToExclude: TypeMapMarker[] = [];

    get displayedMarkers(): MarkerGoogleMaps[] {
        return this._displayedMarkers;
    }
    set displayedMarkers(value: MarkerGoogleMaps[]) {
        this._displayedMarkers = value;
    }


    constructor(
        private formBuilder: FormBuilder,
        private mapsFacade: MapsFacade,
        private entitiesFacade: EntitiesFacade,
        private provisionsFacade: ProvisionsFacade,
    ) {
        // LIST BASED ON MODE
        effect(() => {
            if (this.typeMarkers() === this.providers) {
                this.typesToExclude = [
                    TypeMapMarker.CLUSTER,
                    TypeMapMarker.PATIENT_ACTIVE,
                    TypeMapMarker.PATIENT_INACTIVE,
                ];
                this.modeProviders = true;
                this.modePatients = false;
            } else {
                this.typesToExclude = [
                    TypeMapMarker.CLUSTER,
                    TypeMapMarker.PROVIDER_ACTIVE_ADDRESS,
                    TypeMapMarker.PROVIDER_ACTIVE_ZONE,
                    TypeMapMarker.PROVIDER_INACTIVE_ADDRESS,
                    TypeMapMarker.PROVIDER_INACTIVE_ZONE,
                    TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ADDRESS,
                    TypeMapMarker.PROVIDER_INACTIVE_POSSIBLE_ZONE,
                    TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ADDRESS,
                    TypeMapMarker.PROVIDER_INACTIVE_CANCELLED_ZONE,
                ];
                this.modeProviders = false;
                this.modePatients = true;
            }
            this.updateMarkersList()
        });

        // on filter Providers by zone/type
        effect(() => {
            if (this.typeMarkers() !== this.patients) { // Fixed the return statement
                if (this.locationsFiltered()) {
                    this.updateMarkersList()
                }
            }
        });

        // Track center changes; cache the last valid center and re-sort when it changes
        effect(() => {
            const c = this.actualCenter();
            if (c) {
                this.lastCenter = c;
                this.updateMarkersList()
            }
        });

        // //Callback on input Patients Markers List
        effect(() => {
            if (this.patientsList() && this.patientsList().length > 0) {
                this.updateMarkersList();
            }
        });

        effect(() => {
            if (this.reset() === true) {
                this.localMarkers = [];
                this.updateMarkersList()
                this.selectForm.get(this.SEARCH)!.setValue('', { emitEvent: true });
                this.visibility.clear();
                // for (const m of this.dataSourceMarkers ?? []) {
                //     this.setVisibilityIfChanged(m, true);
                // }
                // this.applyFilter('', true /* reassertVisibility */);
            }
        });

        effect(() => {
            if (!!this.providerAddressVisibility()) {
                const { markerId, value } = this.providerAddressVisibility();
                const marker = this.displayedMarkers.find(m => m.id === markerId);
                if (marker) {
                    this.setVisibilityIfChanged(marker, value);
                }
            }
        });

        effect(() => {
            if (!!this.providerZonesVisibility()) {
                const { markerId, value } = this.providerZonesVisibility();
                let matches = this.displayedMarkers.filter(m => m.oldId === markerId && m.type === TypeMapMarker.PROVIDER_ACTIVE_ZONE);
                matches.forEach(marker => {
                    this.setVisibilityIfChanged(marker, value);
                });
            }
        });

        effect(() => {
            if (!!this.listFocused() && this.listFocused().focus === true) {
                // Focus the search input when the list is focused
                setTimeout(() => {
                    this.searchInputRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    this.searchInputRef?.nativeElement.focus();
                }, 0);
            }
        });

        this.loadingActive$ = this.entitiesFacade.getLoadingByStatusAndLocation$(
            ProviderByStatusAndLocation.ACTIVE_ADDRESS
          );

          this.loadingInactivePossible$ = this.entitiesFacade.getLoadingByStatusAndLocation$(
            ProviderByStatusAndLocation.INACTIVE_POSSIBLE_ADDRESS
          );

          this.loadingInactiveCancelled$ = this.entitiesFacade.getLoadingByStatusAndLocation$(
            ProviderByStatusAndLocation.INACTIVE_CANCELLED_ADDRESS
          );

          this.loadingInactive$ = this.entitiesFacade.getLoadingByStatusAndLocation$(
            ProviderByStatusAndLocation.INACTIVE_ADDRESS
          );

          this.isLoadingGetAgreementsBySpecialtyId$ = this.provisionsFacade.isLoadingGetAgreementsBySpecialtyId$();

          const anyLoading$ = combineLatest([
            this.loadingActive$,
            this.loadingInactivePossible$,
            this.loadingInactiveCancelled$,
            this.loadingInactive$,
            this.isLoadingGetAgreementsBySpecialtyId$,
          ]).pipe(
            map((flags) => flags.some((v) => v === true)),
            distinctUntilChanged()
          );

          this.subscriptions.add(
            anyLoading$.subscribe((value) => {
              this.loading = value;
            })
          );
    }

    ngOnInit(): void {
        if (this.typeMarkers() == this.providers) {
            this._markers = this.mapsFacade.getMarkers$().subscribe((markers) => {
                if (!!markers && markers.length > 0) {
                    this.localMarkers = markers
                        .filter(item => this.isValidMarker(item))
                        .map(m => ({ ...m, visible: m.visible ?? true } as MarkerGoogleMaps));

                    this.updateMarkersList()
                } else {
                    this.localMarkers = [];
                    this.updateMarkersList()
                }
            });

            // Cluster Members subscription
            this._markersMembers = this.mapsFacade.getAllClusterMembers$().subscribe((members) => {
                if (!!members && members.length > 0) {
                    const markersClean = members
                        .filter(item => this.isValidMarker(item))
                        .map(m => ({ ...m, visible: m.visible ?? true } as MarkerGoogleMaps));
                    this.allClusterMembers = markersClean;
                    this.updateMarkersList()
                } else {
                    this.allClusterMembers = [];
                    this.updateMarkersList()
                }
            });
        }
        this.selectForm = this.formBuilder.group({
            [this.SEARCH]: new FormControl(''),
        });

        this.selectForm.get(this.SEARCH)!.valueChanges
            .pipe(
                startWith(this.selectForm.get(this.SEARCH)!.value ?? ''),
                debounceTime(250),
                distinctUntilChanged(),
                map((v) => this.normalize(v))
            )
            .subscribe((string) => {
                // Reapply visibility ONLY when the text changes
                this.applyFilter(string, true /* reassertVisibility */);
            });
    }

    ngOnDestroy(): void {
        this._markers?.unsubscribe();
        this._markersMembers?.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    toggleList(): void {
        this.listVisible = !this.listVisible;
    }

    isValidMarker(item: MarkerGoogleMaps): boolean {
        if (!item || item.lat == null || item.lng == null) return false;

        const isValidCluster = item.clusterSize === undefined || item.clusterSize > 0;
        const isExcluded = this.typesToExclude.includes(item.type as TypeMapMarker);
        return isValidCluster && !isExcluded;
    }

    /** Coalesce multiple triggers into a single recomputation per animation frame */
    //   markersUpdate(): void {
    //     if (this.rafId != null) return;
    //     this.rafId = requestAnimationFrame(() => {
    //       this.rafId = null;
    //       this.updateMarkersList();
    //     });
    //   }

    // Recalculate dataSource + displayed
    updateMarkersList(): void {
        let all: MarkerGoogleMaps[] = [];

        if (this.typeMarkers() === 'PATIENTS') {
            // MODO PACIENTES:
            // Usamos la lista que viene por @Input() patientsList
            const patients = this.patientsList() ?? [];
            all = patients.filter(item => this.isValidMarker(item));

        } else {
            // MODO PROVIDERS:
            // Markers del mapa + members de clusters, filtrados por locationsFiltered
            all = [...this.localMarkers, ...this.allClusterMembers];
            all = all.filter(m => this.locationsFiltered().includes(m.type));
        }

        //unique by id
        const seen = new Set<number>();
        const unique: MarkerGoogleMaps[] = [];
        for (const m of all) {
            if (!seen.has(m.id)) {
                seen.add(m.id);
                unique.push(m);
            }
        }
        //sort by distance if applicable (use cached center if actualCenter() is temporarily undefined)
        const activeCenter = this.actualCenter() ?? this.lastCenter;
        this.dataSourceMarkers = activeCenter ? this.sortMarkersByDistance(unique, activeCenter) : unique;
        this.displayedMarkers = [...this.dataSourceMarkers];

        //apply text filter → displayedMarkers (DO NOT reassert visibility)
        // const query = this.normalize(this.selectForm?.get(this.SEARCH)?.value ?? '');
        // this.applyFilter(query, false /* reassertVisibility */);
    }

    //Apply text filter and decide visibility:
    //- reassertVisibility = true  -> (text changed) enforce filter rule
    //  - reassertVisibility = false -> (external changes) DO NOT override user's toggles; use Map
    applyFilter(q: string, reassertVisibility: boolean): void {
        q === '' ? this.matchedMarkers = null : null;
        if (q === '') {
            this.activeSearch = false;
            this.matchedMarkers = [];
        } else {
            this.activeSearch = true;
        }
        const all = this.dataSourceMarkers ?? [];
        const normalizedQ = this.normalize(q);
        const hasQuery = !!normalizedQ;
        this.matchedMarkers = hasQuery
            ? all.filter((m) => this.normalize(m.name).includes(normalizedQ))
            : all;
        this.lastQuery = normalizedQ;

        // Prepare match index for O(1) lookup
        const matchIds = new Set(this.matchedMarkers.map(m => m.id));

        for (const m of all) {
            let desired: boolean;

            if (reassertVisibility) {
                // Text changed: enforce filter rule
                desired = hasQuery ? matchIds.has(m.id) : true;
            } else {
                // External changes: respect user's preference if it exists
                if (this.visibility.has(m.id)) {
                    desired = !!this.visibility.get(m.id);
                } else {
                    // Reasonable policy for new ids:
                    desired = hasQuery ? matchIds.has(m.id) : true;
                }
            }
            this.setVisibilityIfChanged(m, desired);
        }
    }

    /** Avoid unnecessary facade calls if the value is unchanged.
     *  updateMap=true keeps the Map as the source of truth.
     */
    setVisibilityIfChanged(marker: MarkerGoogleMaps, visible: boolean): void {
        const current = !!marker.visible;
        if (current === visible) {
            // Still keep the Map in sync if requested
            this.visibility.set(marker.id, visible);
        }

        marker.visible = visible;
        this.visibility.set(marker.id, visible);

        if (!!marker.clusterId) {
            this.mapsFacade.setClusterMemberVisibility(marker.id, visible);
        } else {
            this.mapsFacade.setVisibleMarker(marker.id, visible);
        }
    }

    // are ALL visible? (over the filtered set)
    isAllVisible(): boolean {
        const rows = this.matchedMarkers?.length > 0 ? this.matchedMarkers : this.displayedMarkers ?? [];
        return rows.length > 0 && rows.every(m => !!m.visible);
    }

    // is there SOME visible? (over the filtered set)
    isSomeVisible(): boolean {
        const rows = this.matchedMarkers?.length > 0 ? this.matchedMarkers : this.displayedMarkers ?? [];
        return rows.some(m => !!m.visible);
    }

    //set bulk visibility for the LISTED (filtered) items
    toggleAllHeader(checked: boolean): void {
        let hasQery: boolean = false;
        if (this.selectForm.get(this.SEARCH).value !== '') {
            let elements = this.matchedMarkers.length > 0 ? this.matchedMarkers : this.displayedMarkers;
            for (const m of elements) {
                this.setVisibilityIfChanged(m, checked);
            }
        } else {
            this.mapsFacade.setVisibleAllMarkers(checked);
            this.mapsFacade.setClusterMembersVisibility(checked);
        }
    }

    // Row click: set individual visibility (and persist preference in Map)
    onToggleMarker(marker: MarkerGoogleMaps, checked: boolean): void {
        this.setVisibilityIfChanged(marker, checked);
    }

    //Location icon click
    onClickLocation(marker: MarkerGoogleMaps): void {
        const payload: onCheckMarker = {
            id: marker.id,
            type: marker.type,
            clusterId: marker.clusterId ?? undefined,
            visible: !!marker.visible,
        };
        this.locateMarker.emit(payload);
    }

    // trackBy for row stability
    trackById = (_: number, row: MarkerGoogleMaps) => row.id;

    // Distance and sorting
    haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
        const toRad = (d: number) => d * (Math.PI / 180);
        const R = 6371; // Earth radius in km
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    // Sort markers by distance from center
    sortMarkersByDistance(markers: MarkerGoogleMaps[], center: MarkerGoogleMaps): MarkerGoogleMaps[] {
        return [...markers].sort((a, b) => {
            const da = this.haversineDistance(center.lat, center.lng, a.lat, a.lng);
            const db = this.haversineDistance(center.lat, center.lng, b.lat, b.lng);
            return da - db;
        });
    }

    // Text normalization for searches
    normalize(s: string): string {
        return (s ?? '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');
    }

    // onCollapseDone(event: AnimationEvent): void {
    //     // Solo cuando termina en estado "expanded"
    //     if (event.toState === 'expanded' && this.listFocused() && this.searchInputRef) {
    //       // Pequeño delay para asegurar que el elemento esté pintado
    //       setTimeout(() => {
    //         this.searchInputRef.nativeElement.focus();
    //       });
    //     }
    //   }
}
